import {
  Box,
  Button,
  Circle,
  Grid,
  Heading,
  HStack,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Card, NumberInput, Select } from "@components/ui-kit";
import { NotFoundProject } from "@components/widgets/ErrorPage";
import { Trans, t } from "@lingui/macro";
import {
  useGetProjectSettingsQuery,
  useUpdateTimeTrackingRatesForProjectMutation,
} from "@src/__generated__/graphql";
import type { WithIdProps } from "@src/components/HOC/with-id/withId";
import { ProjectDetailLoading } from "@src/components/modules/projects/common/project-detail-loading";
import { CardHeaderTrackingIcon } from "@src/components/modules/projects/settings/common";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { onError } from "@src/utils/apollo";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { getQueryParam, getQueryParamOptional } from "@src/utils/routing";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import { createContext, useContext, useState } from "react";
import { CardTimeTrackingRate } from "./CardTimeTrackingRate";
import { ProjectSettingsModel } from "./models";
import { StoreProjectSettings } from "./StoreProjectSettings";
import { TimeTrackingRate } from "./TimeTrackingRate";
import { TableTimeTracking } from "./table/TableTimeTracking";

export const PageProjectSettingsCtx = createContext({} as StoreProjectSettings);

function useData(store: StoreProjectSettings) {
  const { query } = useRouter();

  const id = getQueryParam("id", query);

  const { data, loading } = useGetProjectSettingsQuery({
    variables: { id },
    skip: !id,
    onCompleted({ project }) {
      if (!project) return;
      store.projectSettings = new ProjectSettingsModel(project);
      store.rateCardId.onChange(project.rate_card_id);
      store.setExchangeRateFromProject(Number(project.rate_card_exchange_rate));
      store.fetchRateCards(project.brand.id);
    },
    ...onError(),
  });

  return { data, loading };
}

const PageProjectSettingsInner = observer(function PageProjectSettingsInner({
  id,
}: WithIdProps) {
  const appStore = useAppStore();
  const store = useContext(PageProjectSettingsCtx);
  const projectSettings = store.projectSettings!;
  const router = useRouter();
  const allowRateEdit =
    getQueryParamOptional("force_update", router.query) === "admin" ||
    projectSettings.isTimeAndMaterials;

  const [updateTimeTrackingRatesForProject] =
    useUpdateTimeTrackingRatesForProjectMutation({
      onCompleted() {
        appStore.UIStore.toast({ title: t`Saved`, status: "success" });
      },
      ...onError(),
    });

  const state = useLocalObservable(() => {
    return {
      average_time_tracking_rate: NumberModel.fromDenomValue(
        projectSettings.average_time_tracking_rate ?? 0,
        2,
      ),
      calculatedTimeTrackingRate: NumberModel.fromDenomValue(
        projectSettings.calculated_average_time_tracking_rate ?? 0,
        2,
      ),

      saveForWorkType: (id: string, val: NumberModel) => {
        updateTimeTrackingRatesForProject({
          variables: {
            input: {
              force_update: allowRateEdit,
              project_id: projectSettings.id,
              average_rate:
                state.average_time_tracking_rate.denominatedValue > 0
                  ? state.average_time_tracking_rate.denominatedValue
                  : null,
              time_tracking_rates_per_work_type: [
                { rate: val.denominatedValue, time_tracking_work_type_id: id },
              ],
            },
          },
        });
      },

      saveForAverage: (val: NumberModel) => {
        state.average_time_tracking_rate = val;
        updateTimeTrackingRatesForProject({
          variables: {
            input: {
              project_id: projectSettings.id,
              average_rate:
                val.denominatedValue > 0 ? val.denominatedValue : null,
              time_tracking_rates_per_work_type: [],
            },
          },
        });
      },
    };
  });

  const redirectToBudget = () => {
    router.push({
      pathname: "/projects/budgets",
      query: {
        id: store.projectSettings?.id,
        budget_id: store.projectSettings?.budget_id || "",
      },
    });
  };

  const ratesPerTaskDisabled = allowRateEdit
    ? false
    : appStore.workspaceStore.settings?.moduleSettings.time_tracking &&
      appStore.workspaceStore.settings?.moduleSettings.budgetovac;

  return (
    <Card>
      <Box px="4" borderBottomWidth="thin" borderBottomColor="grey.100">
        <NavigationProject id={id} />
      </Box>

      <Grid
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
        }}
      >
        <Box
          py="4"
          borderRightWidth={{
            base: "0",
            md: "thin",
          }}
        >
          <HStack
            align="center"
            px="6"
            pb="4"
            borderBottomWidth="thin"
            borderBottomColor="blue.100"
            spacing="3"
          >
            <Circle p="1.5" bg="teal.400" size="8">
              <CardHeaderTrackingIcon />
            </Circle>

            <Heading size="md">
              <Trans>Time tracking settings</Trans>
            </Heading>
          </HStack>

          <TableTimeTracking />
        </Box>

        <VStack spacing="4">
          <Box w="full" p="4" borderBottomWidth="thin">
            <Heading mb="2" size="md">
              <Trans>Rate card</Trans>
            </Heading>
            <HStack mb="2" spacing="2">
              <Box w="full">
                <Select
                  options={store.rateCards}
                  value={store.rateCardId.value}
                  onChange={store.rateCardId.onChange}
                />
              </Box>

              {store.showExchangeRateInput && (
                <Box w="full">
                  <NumberInput
                    currency
                    prefix={`1 ${appStore.workspaceStore.settings?.currency.code} =`}
                    prefixStyles={{ w: 16 }}
                    inputStyle={{ paddingLeft: 16 }}
                    suffix={store.selectedRateCard?.currency_iso ?? ""}
                    {...fieldNumberModelToInputProps(store.exchangeRate)}
                  />
                </Box>
              )}

              <Button
                minW="fit-content"
                isLoading={store.updatingRateCard || store.fetchingRateCards}
                onClick={async () => {
                  if (!store.rateCardId.$) return;
                  await store.updateProjectsRateCard(store.rateCardId.$);
                  store.fetchProjectSettings();
                }}
              >
                <Trans>Apply</Trans>
              </Button>
            </HStack>

            {store.rateCardId.$ ? (
              <Button
                onClick={() => {
                  if (!store.rateCardId.$) return;

                  appStore.budgetovacPositionsSetModalStore.modalState.onOpen({
                    positionsSetId: store.rateCardId.$,
                    onSubmit: store.fetchProjectSettings,
                  });
                }}
                variant="link"
              >
                <Trans>Edit this rate card in settings</Trans>
              </Button>
            ) : (
              <Tooltip label={t`Please select rate card first.`}>
                <Button colorScheme="gray" isDisabled size="sm" variant="link">
                  <Trans>Edit this rate card in settings</Trans>
                </Button>
              </Tooltip>
            )}
          </Box>

          <Box w="full">
            <CardTimeTrackingRate
              bordered={false}
              onEdit={
                !store.projectSettings?.isTimeAndMaterials
                  ? redirectToBudget
                  : undefined
              }
              average_time_tracking_rate={state.average_time_tracking_rate}
              onAverageTimeTrackingRateChange={state.saveForAverage}
              calculatedTimeTrackingRate={state.calculatedTimeTrackingRate}
            >
              {projectSettings.timeTrackingRates.map((rate) => (
                <TimeTrackingRate
                  key={`${rate.timeTrackingWorkType.id}-${rate.rate}`}
                  rate={rate}
                  disabled={ratesPerTaskDisabled}
                  onRateChange={state.saveForWorkType}
                />
              ))}
            </CardTimeTrackingRate>
          </Box>
        </VStack>
      </Grid>
    </Card>
  );
});

export const PageProjectSettings = observer(function PageProjectSettings({
  id,
}: WithIdProps) {
  const appStore = useAppStore();
  const [store] = useState(() => {
    return StoreProjectSettings.create(appStore);
  });

  const { data, loading } = useData(store);

  if (loading) {
    return <ProjectDetailLoading id={id} />;
  }

  if (!data?.project) {
    return <NotFoundProject />;
  }

  if (!store.projectSettings) {
    return null;
  }

  return (
    <PageProjectSettingsCtx.Provider value={store}>
      <div className="flex justify-center items-center">
        <div className="w-full">
          <PageProjectSettingsInner id={id} />
        </div>
      </div>
    </PageProjectSettingsCtx.Provider>
  );
});
